




































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";
import { MiraCompany } from "@/data/models/MiraCompanyAdministration";
import { saveMiraCompany } from "@/data/repository/MiraCompanyAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  canSaveCompanyForm,
  clientLabel,
  companyErrorMessage,
  companyFieldError,
  companyFormChanged,
  MiraCompanyForm,
  groupMasterIds,
  membersOfOwnGroup,
  takenClients,
  takenViewingClients,
  toCompanyForm,
  toCompanySave,
  withViewingClient,
} from "@/utils/MiraCompanyAdministration";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** What `show()` resolves with. */
export type MiraCompanyDialogResult = MiraCompany | "CHANGED_MEANWHILE" | null;

/** One entry of the Mandanten picker. */
interface ClientItem {
  value: string;
  text: string;
  /** Why it cannot be picked, or that it brings its group along. */
  note: string;
  disabled: boolean;
}

/**
 * Shows, edits or creates one company of Mira's market comparison (#2610009 C4): its name, the
 * Mandanten it runs and the Mandant that shows it in Mira like a group (#2610112 X6e). IRES Cloud is the authority (`saveMiraCompanyForAdministration`): a Mandant another
 * company already names, a company changed meanwhile and a Mandant that does not exist are refused there;
 * the picker greys out the taken Mandanten first.
 *
 * `show()` resolves with the saved company, `CHANGED_MEANWHILE` when IRES Cloud refused because the
 * company changed since it was loaded (the caller reloads), or null when closed without saving.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class MiraCompanyDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  isEditMode = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: MiraCompany | null = null;
  form: MiraCompanyForm = toCompanyForm(null);
  clients: ClientAdministrationRow[] = [];
  companies: MiraCompany[] = [];
  /** A field shows its error once it was left, never on an untouched new form or while typing. */
  touched: Record<"name" | "clientIds", boolean> = { name: false, clientIds: false };

  private resolve: ((result: MiraCompanyDialogResult) => void) | null = null;

  get isNew(): boolean {
    return this.stored === null;
  }

  get hasChanged(): boolean {
    return companyFormChanged(this.stored, this.form);
  }

  get masters(): Set<string> {
    return groupMasterIds(this.companies);
  }

  get canSave(): boolean {
    return canSaveCompanyForm(this.form, this.masters);
  }

  /** Every Mandant by name; those another active company names are shown but cannot be picked. */
  get clientItems(): ClientItem[] {
    const taken = takenClients(this.companies, this.stored?.id ?? null);
    const inOwnGroup = membersOfOwnGroup(this.stored);
    const byId = new Map(this.clients.map((client) => [client.id, client]));
    // A stored Mandant missing from the list (deleted since) still shows, so it can be removed.
    const ids = [...new Set([...this.clients.map((client) => client.id), ...this.form.clientIds])];
    return ids
      .map((id) => {
        const client = byId.get(id);
        const comesAlong = inOwnGroup.has(id) && !this.form.clientIds.includes(id);
        const isViewer = id === this.form.viewingClientId;
        const note = isViewer
          ? this.$t("admin.miraCompanies.isViewingClient").toString()
          : taken[id]
          ? this.$t("admin.miraCompanies.takenBy", [taken[id]]).toString()
          : comesAlong
          ? this.$t("admin.miraCompanies.inGroup").toString()
          : this.masters.has(id) || client?.isMasterClient
          ? this.$t("admin.miraCompanies.masterNote").toString()
          : "";
        return { value: id, text: clientLabel(client, id), note, disabled: !!taken[id] || comesAlong || isViewer };
      })
      .sort((a, b) => a.text.localeCompare(b.text, "de"));
  }

  /** The viewing client (#2610112 X6e); choosing one takes it off the Mandanten ({@link withViewingClient}). */
  get viewingClientId(): string | null {
    return this.form.viewingClientId;
  }

  set viewingClientId(viewingClientId: string | null) {
    this.form = withViewingClient(this.form, viewingClientId || null);
  }

  /**
   * Every Mandant as a viewing client; one another active company already shows in, and the members this
   * company covers through its Master-Mandant, are shown but cannot be picked.
   */
  get viewerItems(): ClientItem[] {
    const taken = takenViewingClients(this.companies, this.stored?.id ?? null);
    const inOwnGroup = membersOfOwnGroup(this.stored);
    const byId = new Map(this.clients.map((client) => [client.id, client]));
    const ids = [...new Set([...this.clients.map((client) => client.id), ...(this.form.viewingClientId ? [this.form.viewingClientId] : [])])];
    return ids
      .map((id) => {
        const note = taken[id]
          ? this.$t("admin.miraCompanies.viewerTakenBy", [taken[id]]).toString()
          : inOwnGroup.has(id)
          ? this.$t("admin.miraCompanies.inGroup").toString()
          : "";
        return { value: id, text: clientLabel(byId.get(id), id), note, disabled: !!taken[id] || inOwnGroup.has(id) };
      })
      .sort((a, b) => a.text.localeCompare(b.text, "de"));
  }

  /** How many Mandanten the company covers only through its Master-Mandant. */
  get groupMemberCount(): number {
    return [...membersOfOwnGroup(this.stored)].filter((clientId) => !this.form.clientIds.includes(clientId)).length;
  }

  /** A field's message in edit mode only, and only once the field was touched. */
  errorFor(field: "name" | "clientIds"): string[] {
    if (!this.isEditMode || !this.touched[field]) return [];
    const error = companyFieldError(field, this.form, this.masters);
    return error ? [this.$t(`admin.miraCompanies.fieldErrors.${error}`).toString()] : [];
  }

  touch(field: "name" | "clientIds") {
    this.touched[field] = true;
  }

  formatDate(millis: number): string {
    return formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  /**
   * @param company - the company to show, or null to create one.
   * @param clients - every Mandant, for the picker's names.
   * @param companies - the whole list, so the picker can grey out Mandanten another company names.
   */
  show(company: MiraCompany | null, clients: ClientAdministrationRow[], companies: MiraCompany[]): Promise<MiraCompanyDialogResult> {
    this.stored = company ? { ...company, clientIds: [...company.clientIds] } : null;
    this.form = toCompanyForm(company);
    this.clients = clients;
    this.companies = companies;
    this.touched = { name: false, clientIds: false };
    this.isEditMode = company === null;
    this.baseDialog.show({
      title: this.titleText(),
      // No BEARBEITEN for the shared IRES admin login, which IRES Cloud refuses (#2610071).
      positiveButtonText: this.isEditMode || this.mayEdit
        ? this.$t(this.isEditMode ? "common.save" : "settings.buttons.change").toString()
        : null,
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 700,
      persistent: this.isEditMode,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  private titleText(): string {
    if (this.isNew) return this.$t("admin.miraCompanies.dialog.createTitle").toString();
    return this.$t(this.isEditMode ? "admin.miraCompanies.dialog.editTitle" : "admin.miraCompanies.dialog.viewTitle").toString();
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    if (this.isEditMode) {
      this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
    }
  }

  /** BEARBEITEN in view mode, SICHERN in edit mode. */
  onPositive() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.baseDialog.title = this.titleText();
      this.baseDialog.positiveButtonText = this.$t("common.save").toString();
      this.baseDialog.persistent = true;
      return;
    }
    this.onSave();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.isEditMode || !this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged || !this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await saveMiraCompany(toCompanySave(this.stored, this.form));
      SnackbarModule.showSuccess({ key: "admin.miraCompanies.saved" });
      this.close(saved);
    } catch (error) {
      const byId = new Map(this.clients.map((client) => [client.id, client]));
      const message = companyErrorMessage(
        error,
        (clientId) => clientLabel(byId.get(clientId), clientId),
        (companyId) => this.companies.find((company) => company.id === companyId)?.name ?? companyId
      );
      SnackbarModule.showError(message);
      // Someone changed the company meanwhile: close, so the list reloads and the user starts from the new values.
      if (message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraCompanyDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: MiraCompanyDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
